import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Phone, Truck, ChefHat, Sparkles, UserCog, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { downloadReport } from "@/lib/pdf";

const ICONS = { driver: Truck, kitchen: ChefHat, setup: Sparkles, operations: UserCog };

export default function StaffPage() {
  const [staff, setStaff] = useState([]);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api.get("/users/staff").then((r) => setStaff(r.data));
    api.get("/orders").then((r) => setOrders(r.data));
  }, []);

  const assignmentsFor = (s) => {
    return orders.filter((o) =>
      o.driver_id === s.id ||
      (o.setup_team_ids || []).includes(s.id) ||
      (o.kitchen_team_ids || []).includes(s.id)
    );
  };

  const grouped = staff.reduce((acc, s) => { (acc[s.role] = acc[s.role] || []).push(s); return acc; }, {});

  const exportPDF = () => {
    downloadReport({
      title: "Staff & Assignments",
      filename: `Staff-${new Date().toISOString().slice(0, 10)}.pdf`,
      kpis: [
        { label: "Total Staff", value: staff.length },
        { label: "Drivers", value: (grouped.driver || []).length },
        { label: "Kitchen", value: (grouped.kitchen || []).length },
        { label: "Setup", value: (grouped.setup || []).length },
      ],
      sections: [
        {
          title: "Roster",
          columns: ["Name", "Role", "Email", "Phone", "Active Jobs"],
          rows: staff.map((s) => [
            s.name, s.role, s.email, s.phone || "—",
            String(assignmentsFor(s).length),
          ]),
          columnStyles: { 4: { halign: "right" } },
        },
      ],
    });
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="staff-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Crew</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Staff & Assignments</h1>
        </div>
        <Button onClick={exportPDF} data-testid="staff-pdf" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm">
          <Download className="w-4 h-4 mr-1.5" /> Download PDF
        </Button>
      </div>

      <div className="space-y-6">
        {Object.entries(grouped).map(([role, members]) => {
          const Icon = ICONS[role] || UserCog;
          return (
            <div key={role} className="card-lux p-6">
              <div className="flex items-center gap-2 mb-4">
                <Icon className="w-4 h-4 text-[#D4AF37]" />
                <div className="label-eyebrow">{role}</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {members.map((s) => {
                  const assigns = assignmentsFor(s);
                  return (
                    <div key={s.id} className="p-4 rounded-sm bg-[#0a0a0a] border border-[#1a1a1a]" data-testid={`staff-${s.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="font-display text-lg">{s.name}</div>
                          <div className="text-xs text-zinc-500">{s.email}</div>
                        </div>
                        <span className="text-xs text-[#D4AF37]">{assigns.length} job{assigns.length !== 1 ? "s" : ""}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-zinc-500">
                        {s.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {s.phone}</span>}
                      </div>
                      {assigns.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-[#1a1a1a] space-y-1">
                          {assigns.slice(0, 3).map((o) => (
                            <Link key={o.id} to={`/orders/${o.id}`} className="block text-xs text-zinc-400 hover:text-[#D4AF37] truncate">
                              · {o.customer_name} — {new Date(o.event_date).toLocaleDateString()}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
